import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  signIn,
  signUp,
  signOut,
  confirmSignUp,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth';

interface User {
  userId: string;
  username: string;
  email?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  error: string | null;
  login: (username: string, password: string) => Promise<void>;
  signup: (username: string, email: string, password: string) => Promise<void>;
  confirmSignup: (username: string, code: string) => Promise<void>;
  forgotPassword: (username: string) => Promise<void>;
  confirmForgotPassword: (username: string, code: string, newPassword: string) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user is already logged in
  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      const currentUser = await getCurrentUser();
      const session = await fetchAuthSession();

      // For federated (Google) users, extract email from the ID token claims
      const idTokenPayload = session.tokens?.idToken?.payload;
      const email =
        currentUser.signInDetails?.loginId ||
        (idTokenPayload?.email as string | undefined);

      setUser({
        userId: currentUser.userId,
        username: currentUser.username,
        email,
      });
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }

  async function login(username: string, password: string) {
    try {
      setError(null);
      setLoading(true);

      const result = await signIn({ username, password });

      if (result.isSignedIn) {
        await checkAuth();
      } else if (result.nextStep.signInStep === 'CONFIRM_SIGN_UP') {
        throw new Error('Please confirm your account first');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function signup(username: string, email: string, password: string) {
    try {
      setError(null);
      setLoading(true);

      await signUp({
        username,
        password,
        options: {
          userAttributes: {
            email,
          },
        },
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Signup failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function confirmSignup(username: string, code: string) {
    try {
      setError(null);
      setLoading(true);

      await confirmSignUp({ username, confirmationCode: code });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Confirmation failed';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function logout() {
    try {
      setLoading(true);
      await signOut();
      setUser(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Logout failed';
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  async function forgotPassword(username: string) {
    try {
      setError(null);
      setLoading(true);

      await resetPassword({ username });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send password reset code';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  async function confirmForgotPassword(username: string, code: string, newPassword: string) {
    try {
      setError(null);
      setLoading(true);

      await confirmResetPassword({
        username,
        confirmationCode: code,
        newPassword,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reset password';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }

  function clearError() {
    setError(null);
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        signup,
        confirmSignup,
        forgotPassword,
        confirmForgotPassword,
        logout,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
